import React, {
  memo,
  useCallback,
} from 'react';

import {
  PiFileText,
  PiPlayCircleFill,
} from 'react-icons/pi';
import { TbDownload } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';

import audioWave from '../../../../assets/img/audio-wave.jpg';
import AudioPlayer from '../../../../components/AudioPlayer/AudioPlayer';
import BitmovinPlayer
  from '../../../../components/VideoPlayer/BitmovinPlayer/BitmovinPlayer';
import {
  setGlobalCarouselMediaList,
} from '../../../../context/communityReducer';
import { AcceptedDocFileType } from '../../../../shared/file';
import { getSpecificFileIcon } from '../../../../shared/fileUtils';
import { numFormatterInUnit } from '../../../../shared/formatter';
import { useAppDispatch } from '../../../../shared/hooks';
import { getFileExtension } from '../../../../shared/utils';
import {
  IPostMediaItem,
  IPostMediaLayout,
  ISelFile,
} from '../../../../types/communityTypes';
import PostMediaContentVideo
  from '../PostMediaContentVideo/PostMediaContentVideo';

type Props = {
  files: IPostMediaItem[];
  selectedLayout?: IPostMediaLayout;
  renderDummy?: boolean;
  isMessageRender?: boolean;
  isCreatePost?: boolean;
  postedBy?: string;
};

const CreatePostMediaRender: React.FC<Props> = ({
  files,
  selectedLayout = 'carousel',
  renderDummy = false,
  isMessageRender = false,
  isCreatePost = false,
  postedBy,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleClickOnCollage = useCallback(
    (index: number) => {
      if (isCreatePost) return;

      if (files?.length > 1 && selectedLayout === 'collage' && !isCreatePost) {
        const search = new URLSearchParams();

        search.append('initialSlide', index.toString());

        navigate({ search: search.toString() });
        dispatch(setGlobalCarouselMediaList(files));
      }
    },
    [dispatch, files, isCreatePost, navigate, selectedLayout],
  );

  const renderAttachment = useCallback(
    (file: IPostMediaItem, index?: number, style: React.CSSProperties = {}) => {
      if (file.type === 'image') {
        return (
          <div
            onClick={() =>
              typeof index === 'number'
                ? handleClickOnCollage(index)
                : undefined
            }
            style={
              isMessageRender
                ? { width: '100%', aspectRatio: 'auto', border: 'none' }
                : { ...style }
            }
            className={`createpost__attachment createpost__attachment__image
              ${
                selectedLayout === 'carousel'
                  ? ''
                  : 'createpost__attachment__collage-image'
              }`}>
            <img src={file.url} alt={file.name} />
          </div>
        );
      } else if (file.type === 'video') {
        if (selectedLayout === 'collage' || renderDummy || file?.file?.name) {
          return (
            <PostMediaContentVideo
              postedBy={postedBy}
              mediaId={file?._id as string}
              mediaUrl={file.url}
              transcodingJobId={file?.transcodingJob}
              isCompressDone={file?.isCompressDone || false}
              hasError={file?.isCompressError}
              errorDescription={file?.compressErrorDescription}
              onClick={() =>
                typeof index === 'number'
                  ? handleClickOnCollage(index)
                  : undefined
              }
              className="createpost__attachment createpost__attachment__video"
              style={style}
            />
          );
        }

        return (
          <div className="createpost__attachment createpost__attachment__video">
            <BitmovinPlayer
              key={file._id}
              videoUploadedBy={postedBy}
              mediaId={file._id as string}
              isCompressDone={file?.isCompressDone || false}
              mediaUrl={file.url}
              mediaThumb={file?.thumbnail}
              hasError={file?.isCompressError}
              errorDescription={file?.compressErrorDescription}
              isDRMEnabled={false}
              isCompressedUrl={Boolean(file?.compressedVideoUrl)}
              contentId={file?.transcodingJob}
              compressedUrl={file?.compressedVideoUrl}
              hlsUrl={file?.compressedVideoUrlHls}
              dashUrl={file?.compressedVideoUrlDash}
              postId={file?.postId}
            />
          </div>
        );
      } else if (file.type === 'audio') {
        if (selectedLayout === 'collage' || renderDummy) {
          return (
            <div
              onClick={() =>
                typeof index === 'number'
                  ? handleClickOnCollage(index)
                  : undefined
              }
              className="audio-dummy-img"
              style={{ filter: 'brightness(70%)', ...style }}>
              <PiPlayCircleFill />
              {renderAttachment({
                url: audioWave,
                file: undefined as unknown as File,
                name: 'audioWave',
                ext: '.png',
                type: 'image',
              })}
            </div>
          );
        }
        return (
          <div className="createpost__attachment createpost__attachment__audio">
            <AudioPlayer id={`create-post-audio_${file.url}`} url={file.url} />
          </div>
        );
      } else if (file.type === 'file') {
        const ext =
          getFileExtension(
            file?.name || (file?.fileName as string),
          )?.[1]?.toLowerCase?.() ||
          file?.ext ||
          '';
        const icon = getSpecificFileIcon(ext as AcceptedDocFileType);

        return (
          <div
            className=" createpost__attachment createpost__attachment_file"
            onClick={() =>
              typeof index === 'number'
                ? handleClickOnCollage(index)
                : undefined
            }
            style={style}>
            <span className="createpost__attachment_file_icon">
              {icon || <PiFileText />}
            </span>
            <br />
            <div className="createpost__attachment_file_name">
              <p>{file?.name || file?.fileName}</p>
              {ext || file?.ext ? (
                <span>
                  {ext || file?.ext}
                  {file.fileSize
                    ? `- ${numFormatterInUnit(file.fileSize, 0, 2, 'fileSize')}`
                    : null}
                </span>
              ) : null}
            </div>
            {isCreatePost ? null : (
              <div className="createpost__attachment_file_actions-wrapper">
                <button
                  className="createpost__attachment_file_actions-wrapper_button"
                  onClick={() => {
                    window.open(file?.url);
                  }}>
                  <TbDownload />
                </button>
              </div>
            )}
          </div>
        );
      } else {
        const ext =
          getFileExtension(
            file?.name || (file?.fileName as string),
          )?.[1]?.toLowerCase?.() || '';
        const icon = getSpecificFileIcon(ext as AcceptedDocFileType);
        return (
          <div className="seleted-media_wrapper">
            <div
              className="seleted-media_wrapper_media"
              style={{ width: 'calc(100%)' }}>
              <div
                className="seleted-media_wrapper_media_file"
                style={{ width: `calc(100% - 52px)` }}>
                <div className="seleted-media_wrapper_media_file_icon">
                  {icon || <PiFileText />}
                </div>
                <div className="seleted-media_wrapper_media_file_name">
                  <p>{file?.name || file?.fileName}</p>
                  {ext ? <span>{ext}</span> : null}
                  {file.fileSize
                    ? `- ${numFormatterInUnit(file.fileSize, 0, 2, 'fileSize')}`
                    : null}
                </div>
              </div>
              {isMessageRender ? (
                <button
                  className="seleted-media_wrapper_media_download"
                  onClick={() => {
                    window.open(file?.url);
                  }}>
                  <TbDownload />
                </button>
              ) : null}
            </div>
          </div>
        );
      }
    },
    [
      handleClickOnCollage,
      isCreatePost,
      isMessageRender,
      renderDummy,
      selectedLayout,
      postedBy,
    ],
  );

  const displayMediaInCarousel = useCallback(
    (fileList: ISelFile[]) => {
      return (
        <Slider
          className="mangoMediaCarousel"
          easing="linear"
          arrows
          swipeToSlide
          dots={false}
          slidesToShow={1}
          infinite={false}>
          {fileList.map((eachMedia) => (
            <>
              <div>
                {renderAttachment(
                  eachMedia,
                  undefined,
                  eachMedia?.type === 'file' ? { height: '71vw' } : {},
                )}
              </div>
            </>
          ))}
        </Slider>
      );
    },
    [renderAttachment],
  );

  const displaySingleMedia = (filesList: ISelFile[]) => {
    return (
      <>
        {filesList.map((eachMedia, index) => (
          <div>
            {renderAttachment(
              eachMedia,
              index,
              eachMedia?.type === 'file' ? { height: '71vw' } : {},
            )}
          </div>
        ))}
      </>
    );
  };

  const displayTwoMedia = (filesList: ISelFile[]) => {
    return (
      <div className="two-media_container">
        {filesList.map((eachMedia, index) =>
          renderAttachment(eachMedia, index),
        )}
      </div>
    );
  };

  const displayThreeMedia = (filesList: ISelFile[]) => {
    const [firstMediaObj, ...remainingObj] = filesList;
    return (
      <div className="three-media_container">
        <div className="three-media_container_first-media">
          {renderAttachment(firstMediaObj, 0)}
        </div>
        <div className="three-media_container_remaining-media">
          {remainingObj.map((eachMedia, index) =>
            renderAttachment(eachMedia, index + 1),
          )}
        </div>
      </div>
    );
  };

  const displayFourMedia = (filesList: ISelFile[]) => {
    const [firstMediaObj, secMediaObj, ...remainingObj] = filesList;
    return (
      <div className="four-media_container">
        <div className="four-media_container_wrapper">
          {[firstMediaObj, secMediaObj].map((eachMedia, index) =>
            renderAttachment(eachMedia, index),
          )}
        </div>
        <div className="four-media_container_wrapper">
          {remainingObj.map((eachMedia, index) =>
            renderAttachment(eachMedia, index + 2),
          )}
        </div>
      </div>
    );
  };

  const displayFiveMedia = (filesList: ISelFile[]) => {
    const [firstMediaObj, secMediaObj, ...remainingObj] = filesList;
    return (
      <div className="five-media_container">
        <div className="five-media_container_two-media-wrapper">
          {[firstMediaObj, secMediaObj].map((eachMedia, index) =>
            renderAttachment(eachMedia, index, { height: '100%' }),
          )}
        </div>
        <div className="five-media_container_three-media-wrapper">
          {remainingObj.map((eachMedia, index) =>
            renderAttachment(eachMedia, index + 2),
          )}
        </div>
      </div>
    );
  };

  const displayFivePlusMedia = (filesList: ISelFile[]) => {
    const [
      firstMediaObj,
      secMediaObj,
      thirdMediaObj,
      fourthMediaObj,
      fifthMediaObj,
      ...remainingObj
    ] = filesList;
    return (
      <div className="five-media_container">
        <div className="five-media_container_two-media-wrapper">
          {[firstMediaObj, secMediaObj].map((eachMedia, index) =>
            renderAttachment(eachMedia, index, { height: '100%' }),
          )}
        </div>
        <div className="five-media_container_three-media-wrapper five-media_container_remaining-count">
          {[thirdMediaObj, fourthMediaObj, fifthMediaObj].map(
            (eachMedia, index) => renderAttachment(eachMedia, index + 2),
          )}
          <div
            className="createpost__attachment createpost__attachment__image five-media_container_remaining-count_wrapper"
            onClick={() => handleClickOnCollage(5)}>
            +{remainingObj?.length}
          </div>
        </div>
      </div>
    );
  };

  const displayMediaInCollage = (filesList: ISelFile[]) => {
    switch (filesList?.length) {
      case 0:
        return null;
      case 1:
        return displaySingleMedia(filesList);
      case 2:
        return displayTwoMedia(filesList);
      case 3:
        return displayThreeMedia(filesList);
      case 4:
        return displayFourMedia(filesList);
      case 5:
        return displayFiveMedia(filesList);
      default:
        return displayFivePlusMedia(filesList);
    }
  };

  if (selectedLayout === 'carousel' && files?.length > 1 && !renderDummy) {
    return (
      <div className="post-media__contents post-media__contents--carousel">
        {displayMediaInCarousel(files)}
      </div>
    );
  }
  return (
    <div
      className="post-media__contents post-media__contents--collage"
      style={{ width: '100%' }}>
      {displayMediaInCollage(files)}
    </div>
  );
};

export default memo(CreatePostMediaRender);
